import { useEffect, useState } from 'react'
import type { PublicProduct } from '@/types/checkout'
import { gaItem, trackSelectItem, trackViewItem, trackViewItemList } from '@/lib/analytics/ecommerce'
import { ProductDetailsDialog } from './ProductDetailsDialog'
import { ProductPurchaseDialog } from './ProductPurchaseDialog'
import { RedeemPointsDialog } from '@/components/features/points/RedeemPointsDialog'

/** The address parameter that names the open product, on any page mounting the catalog. */
const PRODUCT_PARAM = 'product'

const readProductParam = () => new URLSearchParams(window.location.search).get(PRODUCT_PARAM)

/* Keeps `?product={slug}` in the address while a product is open, so the link
   in the bar is the link to share. replaceState: opening a product is not a
   page, and Back should leave the listing, not close a dialog. */
const writeProductParam = (slug: string | null) => {
  const url = new URL(window.location.href)
  if (slug) url.searchParams.set(PRODUCT_PARAM, slug)
  else url.searchParams.delete(PRODUCT_PARAM)
  const next = url.pathname + url.search + url.hash
  if (next !== window.location.pathname + window.location.search + window.location.hash) {
    window.history.replaceState(window.history.state, '', next)
  }
}

interface ProductDialogsOptions {
  products: PublicProduct[]
  /** The list is on screen (loaded, not failed): the arrival link and the GA list wait for it. */
  ready: boolean
  /** A product to open on arrival, by slug or id; defaults to `?product=` in the address. */
  preselect?: string | null
  listId: string
  trackListView: boolean
  /** After a paid (or free) purchase is closed with "Done". */
  onPurchased?: () => void
}

/**
 * What any list of products that can be bought on the spot shares: the
 * details and purchase dialogs, `?product=` in the address while one is
 * open (and honored once on arrival), and the GA events. The catalog grid
 * and the storefront's own layout both mount it.
 */
export function useProductDialogs({ products, ready, preselect: preselectProp, listId, trackListView, onPurchased }: ProductDialogsOptions) {
  const [preselect] = useState(() => (preselectProp === undefined ? readProductParam() : preselectProp))
  /* One product is open at a time, either read (details) or being bought. */
  const [open, setOpen] = useState<{ product: PublicProduct; view: 'details' | 'purchase' | 'redeem' } | null>(null)
  /* The link is honored once, on the first load; closing the dialog leaves
     the catalog behind it, like any other visit. */
  const [preselectState, setPreselectState] = useState<'pending' | 'done' | 'missing'>(preselect ? 'pending' : 'done')

  useEffect(() => {
    if (preselectState !== 'pending' || !ready) return
    const match = products.find((product) => product.slug === preselect || product.id === preselect)
    if (match) {
      /* Arriving by link shows the product first, sold out or not; "Buy" is
         one click from there. */
      setOpen({ product: match, view: 'details' })
      setPreselectState('done')
    } else {
      setPreselectState('missing')
    }
  }, [preselectState, ready, products, preselect])

  /* GA: the list once it is on screen (again only if it changes), and the product read in the details. */
  const listKey = products.map((product) => product.id).join(',')
  useEffect(() => {
    if (!trackListView || !ready || !listKey) return
    trackViewItemList(listId, listId, products.map((product, index) => gaItem(product, { index })))
    // Keyed by the ids, not the array identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackListView, ready, listKey, listId])

  const viewedProduct = open?.view === 'details' ? open.product : null
  useEffect(() => {
    if (viewedProduct) trackViewItem(gaItem(viewedProduct), viewedProduct.currency)
    // Once per product opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewedProduct?.id])

  const openProduct = (product: PublicProduct, view: 'details' | 'purchase' | 'redeem') => {
    if (!open) trackSelectItem(listId, listId, gaItem(product, { index: products.indexOf(product) }))
    setOpen({ product, view })
    writeProductParam(product.slug || product.id)
  }
  const closeProduct = () => {
    setOpen(null)
    writeProductParam(null)
  }

  const dialogs = (
    <>
      <ProductDetailsDialog
        product={open?.product ?? null}
        open={open?.view === 'details'}
        onOpenChange={(next) => {
          if (!next) closeProduct()
        }}
        onBuy={(product) => openProduct(product, 'purchase')}
        onRedeem={(product) => openProduct(product, 'redeem')}
      />
      <ProductPurchaseDialog
        product={open?.view === 'purchase' ? open.product : null}
        open={open?.view === 'purchase'}
        onOpenChange={(next) => {
          if (!next) closeProduct()
        }}
        onPurchased={onPurchased}
      />
      {/* F-160: paid with points, outside checkout. */}
      <RedeemPointsDialog
        product={open?.view === 'redeem' ? open.product : null}
        open={open?.view === 'redeem'}
        onOpenChange={(next) => {
          if (!next) closeProduct()
        }}
        onRedeemed={onPurchased}
      />
    </>
  )

  return { openProduct, preselectMissing: preselectState === 'missing', dialogs }
}
